import { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Alert, Snackbar } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../../modules/auth/context/AuthContext';
import { useManufacturer } from '../hooks/useManufacturer';
import { FormHeader } from '../components/ManufacturerForm/FormHeader';
import { BasicInfoSection } from '../components/ManufacturerForm/BasicInfoSection';
import { NetworkSettingsSection } from '../components/ManufacturerForm/NetworkSettingsSection';
import { FormActions } from '../components/ManufacturerForm/FormActions';
import { useTenant } from '../../../shared/context/TenantContext';

interface FormValues {
  name: string;
  description: string;
  address: string;
  host: string;
  ip: string;
  port: number;
  tenantId: string;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Nome obrigatório'),
  description: Yup.string().required('Descrição obrigatória'),
  address: Yup.string().required('Endereço obrigatório'),
  host: Yup.string().required('Host obrigatório'),
  ip: Yup.string()
    .matches(/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/, 'Formato de IP inválido')
    .required('IP obrigatório'),
  port: Yup.number()
    .min(1, 'Porta deve ser entre 1 e 65535')
    .max(65535, 'Porta deve ser entre 1 e 65535')
    .required('Porta obrigatória'),
});

export default function ManufacturerFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user: authUser } = useAuth();
  const { selectedTenant } = useTenant();
  const { 
    manufacturerData,
    isDataLoading,
    createManufacturer,
    updateManufacturer,
    isSubmitting,
    error,
    setError
  } = useManufacturer();
  
  const mode = id ? 'edit' : 'create';



  const initialValues: FormValues = {
    name: manufacturerData?.name || '',
    description: manufacturerData?.description || '',
    address: manufacturerData?.address || '',
    host: manufacturerData?.host || '',
    ip: manufacturerData?.ip || '',
    port: manufacturerData?.port || 8080,
    tenantId: selectedTenant,
  };

  const handleBack = () => {
    navigate('/manufacturers');
  };

  const handleCloseError = () => {
    setError(null);
  };

  if (isDataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Formik<FormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={async (values) => {
          const payload = {
            name: values.name,
            description: values.description,
            address: values.address,
            host: values.host,
            ip: values.ip,
            port: Number(values.port),
            tenant_id: selectedTenant,
            tenantId: selectedTenant,
          };

          if (mode === 'create') {
            await createManufacturer(payload);
          } else {
            await updateManufacturer(id!, payload);
          }
        }}
      >
        {({ touched, errors }) => (
          <Form>
            <Box sx={{ 
              bgcolor: 'background.paper', 
              borderRadius: 3, 
              p: 4, 
              maxWidth: 800, 
              mx: 'auto',
              boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)',
              mt: 4
            }}>
              <FormHeader mode={mode} />

              <Grid container spacing={3}>
                <BasicInfoSection 
                  isSubmitting={isSubmitting}
                  touched={touched}
                  errors={errors}
                />

                <NetworkSettingsSection 
                  isSubmitting={isSubmitting}
                  touched={touched}
                  errors={errors}
                />
              </Grid>

              <FormActions 
                mode={mode}
                isSubmitting={isSubmitting}
                onBack={handleBack}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
}